import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import {
  SpeakerPhone as SpeakerPhoneIcon,
  WarningAmber as WarningAmberIcon,
  InfoOutlined as InfoOutlinedIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
  DeviceThermostat as DeviceThermostatIcon,
} from "@mui/icons-material";
import { useTheme, Alert } from "@mui/material";

import { toggleHasAlertBanner } from "app/store/transmission/slice/transmissionSlice";
import { useGetAlertBannerQuery } from "app/store/transmission/api/transmissionTableApi";

import { getInitials } from "utils/string";
import { formatLocalDateTime } from "utils/date";
import DeactivatedPatientBanner from "components/pages/transmissions/modal/detail/alert/DeactivatedPatientBanner";

const AlertBanner = ({ serialNumber }) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const { data, isLoading, isFetching } = useGetAlertBannerQuery(serialNumber, {
    refetchOnMountOrArgChange: true,
  });

  const [alertBanner, setAlertBanner] = useState(false);

  useEffect(() => {
    if (data) {
      const alertBanner =
        data?.bannerText ||
        data?.preferredLanguage ||
        data?.deviceAlertText?.length > 0 ||
        data?.leadAlertText?.length > 0 ||
        data?.subscriptionPausedText ||
        data?.subscriptionRemoveText ||
        data?.monthlyBatteryCheckData ||
        data?.deactivatedInfo ||
        data?.inOfficeOnlyText;

      setAlertBanner(alertBanner);
      dispatch(toggleHasAlertBanner(Boolean(alertBanner)));
    }

    return () => dispatch(toggleHasAlertBanner(false));
  }, [data, dispatch]);

  return (
    (!isLoading || !isFetching) &&
    alertBanner &&
    (data?.deactivatedInfo ? (
      <DeactivatedPatientBanner data={data} />
    ) : (
      <Alert
        severity="warning"
        variant="outlined"
        className="dialog-content-alert"
        icon={false}
        style={{
          margin: 0,
          backgroundColor:
            theme.palette.type === "dark" ? "#4B371A" : "#fff4e5",
        }}
      >
        <span className="dialog-content-alert-text">
          {[
            data?.deactivatedInfo && (
              <span key="deactivatedInfo">
                <InfoOutlinedIcon
                  style={{ verticalAlign: "sub" }}
                  className="mr-2 ml-2"
                />
                INACTIVE
                {data?.deactivatedInfo?.reason &&
                  ` - ${data.deactivatedInfo?.reason}`}
                {data?.deactivatedInfo?.updated_at &&
                  ` (Deactivated on ${formatLocalDateTime(data.deactivatedInfo?.updated_at)}`}
                {data.deactivatedInfo?.updated_by &&
                  ` by ${getInitials(data.deactivatedInfo.updated_by?.name, " ")})`}
              </span>
            ),
            data?.bannerText && (
              <span key="banner">
                <WarningAmberIcon
                  style={{
                    verticalAlign: "sub",
                    marginRight: "8px",
                    marginLeft: "8px",
                  }}
                />
                {data.bannerText}
              </span>
            ),
            data?.preferredLanguage && (
              <span key="preferredLanguage">
                <RecordVoiceOverIcon
                  style={{
                    verticalAlign: "sub",
                    marginRight: "8px",
                    marginLeft: "8px",
                  }}
                />
                Patient speaks {data.preferredLanguage}
              </span>
            ),
            data?.deviceAlertText?.length > 0 && (
              <span key="deviceAlertText">
                <SpeakerPhoneIcon
                  style={{
                    verticalAlign: "sub",
                    marginRight: "8px",
                  }}
                />
                <span
                  style={{
                    verticalAlign: "middle",
                    marginRight: "8px",
                  }}
                >
                  {data?.deviceAlertText}
                </span>
              </span>
            ),
            data?.leadAlertText?.length > 0 && (
              <span key="leadAlertText">
                <DeviceThermostatIcon
                  style={{
                    verticalAlign: "sub",
                    marginRight: "8px",
                  }}
                />
                <span
                  style={{
                    verticalAlign: "middle",
                    marginRight: "8px",
                  }}
                >
                  {data?.leadAlertText.join(" | ")}
                </span>
              </span>
            ),
            data?.inOfficeOnlyText && (
              <span key="inOfficeOnlyText">{data?.inOfficeOnlyText}</span>
            ),
            data?.subscriptionPausedText && (
              <span key="subscriptionPausedText">
                {data.subscriptionPausedText}
              </span>
            ),
            data?.subscriptionRemoveText && (
              <span key="subscriptionRemoveText">
                {data.subscriptionRemoveText}
              </span>
            ),
            data?.monthlyBatteryCheckData && (
              <span key="monthlyBatteryCheckData">
                Monthly Battery Checks -{" "}
                {formatLocalDateTime(
                  data?.monthlyBatteryCheckData?.monthlyBatteryCheckUpdatedAt
                )}{" "}
                {data?.monthlyBatteryCheckData?.userNameInitials}
              </span>
            ),
          ]
            .filter(Boolean)
            .reduce((acc, curr, idx, arr) => {
              if (idx > 0) {
                acc.push(
                  <span key={`separator-${idx}`} style={{ margin: "0 8px" }}>
                    |
                  </span>
                );
              }
              acc.push(curr);
              return acc;
            }, [])}
        </span>
      </Alert>
    ))
  );
};

export default AlertBanner;
